
import React from 'react';
import { Lock, Edit3 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useEditingLocks } from '@/hooks/useEditingLocks';

interface FieldLockIndicatorProps {
  entityType: 'card' | 'column' | 'board';
  entityId: string;
  fieldName?: string;
  children: React.ReactNode;
  disabled?: boolean;
}

export function FieldLockIndicator({
  entityType,
  entityId,
  fieldName,
  children,
  disabled = false
}: FieldLockIndicatorProps) {
  const { isFieldLocked, getFieldLock } = useEditingLocks({ 
    entityType, 
    entityId, 
    enabled: !disabled 
  });
  
  const locked = isFieldLocked(fieldName);
  const lockInfo = getFieldLock(fieldName);

  if (!locked) {
    return <>{children}</>;
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="relative">
            {children}
            {/* Lock overlay */}
            <div className="absolute inset-0 bg-yellow-50/80 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded flex items-center justify-center backdrop-blur-sm">
              <Badge variant="secondary" className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                <Lock className="h-3 w-3 mr-1" />
                Editing...
              </Badge>
            </div>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <div className="text-sm">
            <div className="font-medium">{lockInfo?.user_name || 'Another user'} is editing this field</div>
            <div className="text-xs text-gray-500 mt-1">
              Started {new Date(lockInfo?.locked_at || '').toLocaleTimeString()}
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
